import type { Segment, SegmentRule, VoterDataCatalogDto, VoterDataFieldDto } from '@signalvote/shared';
import { ListPurpose, VOTER_DATA_MAX_AREAS_PER_SEGMENT, VOTER_DATA_MAX_PLACES_PER_SEGMENT, VoterDataFieldKind, VoterDataGeography } from '@signalvote/shared';
import { isDefaultOf } from './voterPurpose';

/**
 * The list being built, as it rides in the URL and in this tab's session storage, so a reload,
 * Back, or a shared link brings it back. It names its campaign: Segments are campaign-scoped, so a
 * list is never restored into another campaign. Restoring trusts none of it and checks every part
 * against the catalog, the same vocabulary a SegmentRule set by hand must use.
 */
export interface SavedList {
  campaign: string;
  state: string;
  /** The areas stacked, in the order added; each keeps at least one district or place. None is the whole state. */
  areas?: PickedArea[];
  purpose: ListPurpose;
  rules: SavedRule[];
}

/** One area of the list: the districts or places of one kind it keeps. The list keeps only voters in every area. */
export interface PickedArea {
  geography: VoterDataGeography;
  places: PickedPlace[];
}

/**
 * A district or place in the list's area, by the code the server sends and takes back. A place's
 * name comes from the search it was picked from, so it is kept with it; a district's comes from the
 * catalog, and a place known only by code, such as a quoted order's, is looked up.
 */
export interface PickedPlace {
  code: string;
  label?: string;
}

/** `purposeDefault` marks a rule the purpose added and the customer never changed; without it a rule is the customer's. */
export type SavedRule = { field: string; values: string[]; purposeDefault?: true } | { field: string; voted: boolean };

/** The builder's own state for a list, ready to set. */
export interface VoterDraft {
  state: string;
  areas: PickedArea[];
  rules: SegmentRule[];
  purpose: ListPurpose;
  /** The ids of rules the purpose added, so switching purpose can tell them from the customer's. */
  defaultRuleIds: ReadonlySet<string>;
}

/** Longer than this, a list stays out of the URL and is kept in session storage only. */
const MAX_URL_CHARS = 1500;

const STORAGE_PREFIX = 'voterDataList:';

const isString = (value: unknown): value is string => typeof value === 'string';
const isOneOf = <T extends string>(values: Record<string, T>, value: unknown): value is T => Object.values(values).includes(value as T);

function parseRule(raw: unknown): SavedRule | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { field, values, voted, purposeDefault } = raw as Record<string, unknown>;
  if (!isString(field)) return null;
  if (typeof voted === 'boolean') return { field, voted };
  if (Array.isArray(values) && values.every(isString)) return purposeDefault === true ? { field, values, purposeDefault } : { field, values };
  return null;
}

function parsePlace(raw: unknown): PickedPlace | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { code, label } = raw as Record<string, unknown>;
  if (!isString(code) || !code) return null;
  return isString(label) ? { code, label } : { code };
}

function parseArea(geography: unknown, places: unknown): PickedArea | null {
  if (!isOneOf(VoterDataGeography, geography) || !Array.isArray(places)) return null;
  const picked = places.map(parsePlace).filter((p): p is PickedPlace => p !== null);
  return picked.length ? { geography, places: picked } : null;
}

/** A list's areas in any shape it was saved in: `areas`, or before areas stacked one `area` with `places`, or one `district`. */
function parseAreas({ areas, area, places, district }: Record<string, unknown>): PickedArea[] {
  if (Array.isArray(areas)) {
    return areas.flatMap((a) => {
      if (typeof a !== 'object' || a === null) return [];
      const { geography, places } = a as Record<string, unknown>;
      return parseArea(geography, places) ?? [];
    });
  }
  const one = parseArea(area, Array.isArray(places) ? places : isString(district) ? [{ code: district }] : null);
  return one ? [one] : [];
}

/**
 * Reads a saved list's shape from anything: the URL's search or session storage. Its contents are
 * checked on restore.
 */
export function parseSavedList(raw: unknown): SavedList | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const fields = raw as Record<string, unknown>;
  const { campaign, state, purpose, rules } = fields;
  if (!isString(campaign) || !isString(state) || !isOneOf(ListPurpose, purpose) || !Array.isArray(rules)) return undefined;
  const areas = parseAreas(fields);
  return {
    campaign,
    state,
    ...(areas.length ? { areas } : {}),
    purpose,
    rules: rules.map(parseRule).filter((r): r is SavedRule => r !== null),
  };
}

export function savedListOf(campaign: string, draft: VoterDraft): SavedList {
  return {
    campaign,
    state: draft.state,
    ...(draft.areas.length ? { areas: draft.areas } : {}),
    purpose: draft.purpose,
    rules: draft.rules.map((r) =>
      r.kind === 'Voted'
        ? { field: r.fieldId, voted: r.voted }
        : { field: r.fieldId, values: r.values, ...(draft.defaultRuleIds.has(r.id) ? { purposeDefault: true as const } : {}) },
    ),
  };
}

export const fitsInUrl = (list: SavedList) => JSON.stringify(list).length <= MAX_URL_CHARS;

function ruleOf(field: VoterDataFieldDto, saved: SavedRule, id: string): SegmentRule | null {
  if (field.kind === VoterDataFieldKind.Vote) return 'voted' in saved ? { id, fieldId: field.id, kind: 'Voted', voted: saved.voted } : null;
  if (!('values' in saved)) return null;
  const values = field.options.filter((o) => saved.values.includes(o.label)).map((o) => o.label);
  const keepsSomeone = field.options.some((o) => o.selectable && values.includes(o.label));
  if (!keepsSomeone || values.length === field.options.length) return null;
  return { id, fieldId: field.id, kind: 'Values', values };
}

/**
 * The draft a saved list restores to, or null when it belongs to another campaign or names no
 * purpose the catalog offers. Unknown fields, options and areas are dropped quietly, as are rules
 * past L2's filter cap. A rule marked as the purpose's default stays one only if it still is.
 */
export function restoreList(saved: SavedList | undefined, campaign: string, catalog: VoterDataCatalogDto, newId: () => string): VoterDraft | null {
  if (!saved || saved.campaign !== campaign) return null;
  const purpose = catalog.purposes.find((p) => p.purpose === saved.purpose);
  if (!purpose) return null;
  const state = catalog.states.some((s) => s.code === saved.state) ? saved.state : '';
  const areas = state ? withinAreaLimits(saved.areas ?? []) : [];

  const fields = new Map(catalog.categories.flatMap((c) => c.fields).map((f) => [f.id, f]));
  const room = catalog.maxFilters - 1 - areas.length;
  const rules: SegmentRule[] = [];
  const defaultRuleIds = new Set<string>();
  for (const savedRule of saved.rules) {
    const field = fields.get(savedRule.field);
    if (!field || rules.length >= room || rules.some((r) => r.fieldId === field.id)) continue;
    const rule = ruleOf(field, savedRule, newId());
    if (!rule) continue;
    rules.push(rule);
    if ('purposeDefault' in savedRule && savedRule.purposeDefault && isDefaultOf(purpose, rule)) defaultRuleIds.add(rule.id);
  }
  return { state, areas, rules, purpose: purpose.purpose, defaultRuleIds };
}

/** The areas a list may hold: one of each kind, at most `VOTER_DATA_MAX_AREAS_PER_SEGMENT`, `VOTER_DATA_MAX_PLACES_PER_SEGMENT` codes in all. */
function withinAreaLimits(areas: PickedArea[]): PickedArea[] {
  let left = VOTER_DATA_MAX_PLACES_PER_SEGMENT;
  const kept: PickedArea[] = [];
  for (const area of areas) {
    if (kept.length === VOTER_DATA_MAX_AREAS_PER_SEGMENT || left === 0 || kept.some((a) => a.geography === area.geography)) continue;
    const places = area.places.slice(0, left);
    left -= places.length;
    kept.push({ geography: area.geography, places });
  }
  return kept;
}

export function readTabList(campaign: string): SavedList | undefined {
  try {
    const stored = sessionStorage.getItem(STORAGE_PREFIX + campaign);
    return stored ? parseSavedList(JSON.parse(stored)) : undefined;
  } catch {
    return undefined;
  }
}

export function writeTabList(list: SavedList): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + list.campaign, JSON.stringify(list));
  } catch {
    // Storage full or blocked: the URL still carries a list that fits.
  }
}

/** The builder's state for a list the server already holds, such as a quoted order's Segment, with rule ids of the builder's own. */
export function draftOfSegment(segment: Segment, newId: () => string): VoterDraft {
  return {
    state: segment.state ?? '',
    areas: segment.areas.map((a) => ({ geography: a.geography, places: a.codes.map((code) => ({ code })) })),
    rules: (segment.rules ?? []).map((rule) => ({ ...rule, id: newId() })),
    purpose: segment.purpose,
    defaultRuleIds: new Set(),
  };
}

/**
 * The Draft order this tab placed for the list it is building, with the token it holds, so reopening
 * the order dialog, or a reload, re-quotes that Draft instead of placing another.
 */
export interface PlacedDraft {
  id: string;
  concurrencyVersion: number;
}

const DRAFT_PREFIX = 'voterDataDraft:';

export function readTabDraft(campaign: string): PlacedDraft | undefined {
  try {
    const stored = sessionStorage.getItem(DRAFT_PREFIX + campaign);
    const raw: unknown = stored ? JSON.parse(stored) : undefined;
    if (typeof raw !== 'object' || raw === null) return undefined;
    const { id, concurrencyVersion } = raw as Record<string, unknown>;
    return isString(id) && typeof concurrencyVersion === 'number' ? { id, concurrencyVersion } : undefined;
  } catch {
    return undefined;
  }
}

/** Remembers the Draft, or forgets it when `draft` is undefined. */
export function writeTabDraft(campaign: string, draft: PlacedDraft | undefined): void {
  try {
    if (draft) sessionStorage.setItem(DRAFT_PREFIX + campaign, JSON.stringify(draft));
    else sessionStorage.removeItem(DRAFT_PREFIX + campaign);
  } catch {
    // Storage full or blocked: the next order places a new Draft, as it did before.
  }
}

const DISMISSED_PREFIX = 'voterDataSuggestionDismissed:';

/** The suggested district "Not this one" turned down in this tab, as `state:kind:code`, so it isn't offered again. */
export function readDismissedSuggestion(campaign: string | null): string | null {
  try {
    return campaign ? sessionStorage.getItem(DISMISSED_PREFIX + campaign) : null;
  } catch {
    return null;
  }
}

export function writeDismissedSuggestion(campaign: string | null, key: string): void {
  try {
    if (campaign) sessionStorage.setItem(DISMISSED_PREFIX + campaign, key);
  } catch {
    // Storage blocked: the card stays hidden until the page reloads.
  }
}
